import styles from './SuggestionWrapper.module.css';
import {
    getImplantPreview,
    getSearchSuggestions,
} from '../../../services/apiService';
import { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFolder,
    faGear,
    faList,
    faNoteSticky,
    faXRay,
} from '@fortawesome/free-solid-svg-icons';
import SuggestionPreview from './preview/SuggestionPreview';
import Button, { ButtonType } from '../../general-components/Button';
import Status from '../../general-components/Status';
import GppGoodIcon from '@mui/icons-material/GppGood';
import AuthContext from '../../../store/auth-context';
import { Tooltip } from '@mui/material';
import { EVENT_KEYS, trackClick } from '../../../services/api/trackingService';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useTranslation } from 'react-i18next';

const SuggestionWrapper: React.FC<{ term: string }> = ({ term }) => {
    const { t } = useTranslation();
    const [results, setResults] = useState<any[]>([]);
    const [previewType, setPreviewType] = useState<{
        type: '' | 'implant' | 'type' | 'manufacturer' | 'folder' | 'policy';
        id: string;
    }>({ type: '', id: '' });
    const [hoveredId, setHoveredId] = useState(0);

    const ctx = useContext(AuthContext);

    const search = async (term: string) => {
        const res = await getSearchSuggestions(term);
        setResults(res);
    };

    const incrementHover = () => {
        if (hoveredId < 9 && hoveredId < results.length) {
            setHoveredId(hoveredId + 1);
        }
    };

    const decrementHover = () => {
        if (hoveredId > 0) {
            setHoveredId(hoveredId - 1);
        }
    };
    const matches = useMediaQuery('(min-width: 768px)');

    useEffect(() => {
        const handleKeyDown = (event: any) => {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                incrementHover();
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                decrementHover();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    useEffect(() => {
        if (term.length >= 3) {
            search(term);
        } else {
            setResults([]);
            setPreviewType({ type: '', id: '' });
        }
    }, [term]);

    const updatePreviewType = async () => {
        if (results.length > 0) {
            if (results[hoveredId].implant_id) {
                setPreviewType({
                    type: 'implant',
                    id: results[hoveredId].implant_id,
                });
            }
            if (results[hoveredId].manufacturer_id) {
                setPreviewType({
                    type: 'manufacturer',
                    id: results[hoveredId].manufacturer_id,
                });
            }
            if (results[hoveredId].type_name) {
                setPreviewType({
                    type: 'type',
                    id: results[hoveredId].type_name,
                });
            }
            if (results[hoveredId].folder_id) {
                setPreviewType({
                    type: 'folder',
                    id: results[hoveredId].folder_id,
                });
            }
            if (results[hoveredId].policy_id) {
                setPreviewType({
                    type: 'policy',
                    id: results[hoveredId].policy_id,
                });
            }
        } else {
            setPreviewType({ type: '', id: '' });
        }
    };

    useEffect(() => {
        updatePreviewType();
    }, [results, hoveredId]);

    useEffect(() => {
        if (hoveredId >= results.length) {
            setHoveredId(0);
        }
    }, [results]);

    if (results.length > 0) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.listWrapper}>
                    <div id={'result-list'} className={styles.list}>
                        {results.map((r, index) => (
                            <div
                                onMouseEnter={() => setHoveredId(index)}
                                // onMouseLeave={handleMouseLeave(index)}
                                key={index}
                                className={`${styles.result} ${
                                    hoveredId === index ? styles.hover : ''
                                }`}
                            >
                                {r.implant_model && (
                                    <a
                                        onClick={() => {
                                            trackClick(
                                                EVENT_KEYS.clickSuggestion,
                                                {
                                                    searchTerm: term,
                                                    suggestionType: 'implant',
                                                    value: r.implant_model,
                                                }
                                            );
                                        }}
                                        href={'/implant/' + r.implant_id}
                                        className={styles.implantLink}
                                    >
                                        <div className={styles.icon}>
                                            <Status
                                                status={r.implant_status}
                                                small
                                            />
                                        </div>
                                        <p>{r.implant_model}</p>
                                        <div className={styles.approved}>
                                            {r.implant_approvals?.includes(
                                                ctx.getContext().department._id
                                            ) && (
                                                <Tooltip
                                                    title={t(
                                                        'search.suggestionWrapper.tooltips.approvedImplant',
                                                        {
                                                            departmentName:
                                                                ctx.getContext()
                                                                    .department
                                                                    .name,
                                                        }
                                                    )}
                                                >
                                                    <GppGoodIcon />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </a>
                                )}
                                {r.type_name && (
                                    <a
                                        onClick={() => {
                                            trackClick(
                                                EVENT_KEYS.clickSuggestion,
                                                {
                                                    searchTerm: term,
                                                    suggestionType:
                                                        'implantType',
                                                    value: r.type_name,
                                                }
                                            );
                                        }}
                                        href={'/all?type=' + r.type_name}
                                        className={styles.implantLink}
                                    >
                                        <p>
                                            <FontAwesomeIcon icon={faList} />
                                            {r.type_name}
                                        </p>
                                    </a>
                                )}
                                {r.manufacturer_name && (
                                    <a
                                        onClick={() => {
                                            trackClick(
                                                EVENT_KEYS.clickSuggestion,
                                                {
                                                    searchTerm: term,
                                                    suggestionType:
                                                        'manufacturer',
                                                    value: r.manufacturer_name,
                                                }
                                            );
                                        }}
                                        href={
                                            '/all?manufacturer=' +
                                            r.manufacturer_id
                                        }
                                        className={styles.implantLink}
                                    >
                                        <p>
                                            <FontAwesomeIcon icon={faGear} />
                                            {r.manufacturer_name}
                                        </p>
                                    </a>
                                )}
                                {r.folder_title && (
                                    <a
                                        onClick={() => {
                                            trackClick(
                                                EVENT_KEYS.clickSuggestion,
                                                {
                                                    searchTerm: term,
                                                    suggestionType: 'folder',
                                                    value: r.folder_title,
                                                }
                                            );
                                        }}
                                        href={'/group/' + r.folder_id}
                                        className={styles.implantLink}
                                    >
                                        <div className="flex min-w-0 items-start">
                                            <FontAwesomeIcon icon={faFolder} />
                                            <div className="min-w-0">
                                                <div className="break-words leading-4">
                                                    {r.folder_title}
                                                </div>
                                                {r.folder_parentTitle && (
                                                    <div className="mt-1 truncate text-xs font-medium text-subtle-foreground">
                                                        {t(
                                                            'folders.search.inParent',
                                                            {
                                                                parent: r.folder_parentTitle,
                                                            }
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </a>
                                )}
                                {r.policy_title && (
                                    <a
                                        onClick={() => {
                                            trackClick(
                                                EVENT_KEYS.clickSuggestion,
                                                {
                                                    searchTerm: term,
                                                    suggestionType: 'policy',
                                                    value: r.policy_title,
                                                }
                                            );
                                        }}
                                        href={
                                            '/department-policies/' +
                                            r.policy_id
                                        }
                                        className={styles.implantLink}
                                    >
                                        <p>
                                            <FontAwesomeIcon
                                                icon={faNoteSticky}
                                            />
                                            {r.policy_title}
                                        </p>
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className={styles.seeResults}>
                        <Button
                            type={ButtonType.secondary}
                            onClick={() => {
                                window.location.href =
                                    '/search/query?term=' + term;
                            }}
                        >
                            {t(
                                'search.suggestionWrapper.buttons.seeAllResults'
                            )}
                        </Button>
                    </div>
                </div>
                {matches && (
                    <div className={styles.preview}>
                        <SuggestionPreview
                            type={previewType.type}
                            id={previewType.id}
                        />
                    </div>
                )}
            </div>
        );
    }
    if (results.length === 0) {
        return (
            <div className={styles.noResult}>
                <h3>{t('search.suggestionWrapper.noResults.title')}</h3>
            </div>
        );
    }
};

export default SuggestionWrapper;
